(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  let activePanel = null;
  let opener = null;
  let savedScroll = null;
  const scrollOwners = new Set();
  let privacyActive = false;
  let privacyReturnTarget = null;
  let privacyTrigger = null;
  let privacyRequestPending = false;
  let nativePrivacySnapshot = null;
  let lastOutsidePrivacyFocus = document.activeElement;
  let busy = false;
  let closeTimer;
  let refreshVersion = 0;
  let cartNeedsRefresh = false;
  let reading = false;
  let panelOpening = false;
  const focusable = 'button:not([disabled]),a[href],input:not([disabled]),select,textarea,[tabindex="0"]';

  function lockPage(owner = 'panel', originalState = null) {
    scrollOwners.add(owner);
    if (savedScroll) return;
    document.dispatchEvent(new CustomEvent('llukes:overlay', { detail: { open: true } }));
    savedScroll = originalState || { x: window.scrollX, y: window.scrollY, style: document.body.getAttribute('style') };
    document.body.classList.add('llukes-panel-open');
    Object.assign(document.body.style, { position: 'fixed', top: `${-savedScroll.y}px`, left: `${-savedScroll.x}px`, width: '100%', overflowY: 'hidden' });
  }

  function unlockPage(owner = 'panel') {
    scrollOwners.delete(owner);
    if (scrollOwners.size || !savedScroll) return;
    const saved = savedScroll;
    savedScroll = null;
    if (saved.style === null) document.body.removeAttribute('style');
    else document.body.setAttribute('style', saved.style);
    document.body.classList.remove('llukes-panel-open');
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
    document.dispatchEvent(new CustomEvent('llukes:overlay', { detail: { open: false } }));
  }

  function closePanel(immediate = false, restoreFocus = true) {
    if (!activePanel) return;
    const panel = activePanel;
    clearTimeout(closeTimer);
    panel.classList.remove('is-visible');
    const finish = () => {
      if (activePanel === panel) activePanel = null;
      panel.close();
      unlockPage();
      if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
    };
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else closeTimer = setTimeout(finish, 300);
  }

  function openPanel(id, trigger) {
    if (privacyActive || panelOpening) return;
    const panel = document.getElementById(id);
    if (!(panel instanceof HTMLDialogElement)) return;
    if (activePanel === panel) return;
    const beforeOpen = window.llukesBeforePanelOpen?.(id, trigger);
    const finishOpening = () => {
      if (privacyActive || activePanel === panel) return;
    const returnTarget = activePanel?.contains(trigger) ? opener : trigger || document.activeElement;
    if (activePanel) closePanel(true, false);
    clearTimeout(closeTimer);
    opener = returnTarget;
    lockPage();
    activePanel = panel;
    panel.showModal();
    requestAnimationFrame(() => panel.classList.add('is-visible'));
    panel.querySelector(focusable)?.focus({ preventScroll: true });
    };
    if (!beforeOpen || typeof beforeOpen.then !== 'function') {
      finishOpening();
      return;
    }
    panelOpening = true;
    beforeOpen.then(finishOpening).finally(() => {
      panelOpening = false;
    });
  }

  function showCartError(message) {
    document.querySelectorAll('[data-cart-error]').forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const payload = await response.json();
      return { response, payload };
    } finally {
      clearTimeout(timeout);
    }
  }

  function updateCartAvailability() {
    document.querySelectorAll('[data-cart-content]').forEach((element) => {
      element.inert = busy || reading;
      if (busy || reading) element.setAttribute('aria-busy', 'true');
      else element.removeAttribute('aria-busy');
    });
    document.querySelectorAll('[data-cart-form] [name="checkout"]').forEach((button) => {
      button.disabled = busy || reading || cartNeedsRefresh;
    });
    document.querySelectorAll('[data-cart-reload]').forEach((link) => { link.hidden = !cartNeedsRefresh; });
  }

  function rememberCartFocus() {
    const element = document.activeElement;
    const container = element?.closest('[data-cart-content]');
    if (!container) return null;
    const line = element.closest('[data-cart-line]');
    return {
      element,
      container,
      panel: container.closest('dialog'),
      key: line?.dataset.cartLine,
      index: [...container.querySelectorAll('[data-cart-line]')].indexOf(line),
      selector: element.matches('[data-cart-quantity]') ? '[data-cart-quantity]' : element.matches('[data-cart-remove]') ? '[data-cart-remove]' : element.matches('[name="checkout"]') ? '[name="checkout"]' : null,
    };
  }

  function restoreCartFocus(bookmark) {
    if (!bookmark?.selector || !bookmark.container.isConnected) return;
    if (bookmark.panel && !bookmark.panel.open) return;
    if (document.activeElement !== document.body && document.activeElement !== bookmark.element) return;
    const lines = [...bookmark.container.querySelectorAll('[data-cart-line]')];
    const line = lines.find((item) => item.dataset.cartLine === bookmark.key) || lines[Math.min(bookmark.index, lines.length - 1)];
    const target = line?.querySelector(bookmark.selector) || bookmark.container.querySelector('[name="checkout"]:not(:disabled),a[href],button:not(:disabled)');
    target?.focus({ preventScroll: true });
  }

  async function refreshCart() {
    const version = ++refreshVersion;
    const bookmark = rememberCartFocus();
    reading = true;
    cartNeedsRefresh = true;
    updateCartAvailability();
    try {
      const { response, payload: sections } = await request(`${root}?sections=llukes-cart-content`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart refresh failed');
      const html = sections['llukes-cart-content'];
      if (!html) throw new Error('Cart section unavailable');
      const template = document.createElement('template');
      template.innerHTML = html;
      const count = template.content.querySelector('[data-cart-state]')?.dataset.count;
      if (count === undefined) throw new Error('Cart state unavailable');
      // A newer request or mutation owns the current state, so older reads are discarded.
      if (version !== refreshVersion) return null;
      document.querySelectorAll('[data-cart-content]').forEach((container, index) => {
        container.innerHTML = html.replaceAll('quantity-refresh-', `quantity-render-${index}-`);
        if (container.closest('.llukes-cart-page')) {
          container.querySelector('[data-cart-state]')?.classList.add('llukes-cart-state--page');
          container.querySelector('.llukes-cart-packaging')?.classList.add('llukes-cart-packaging--page');
        }
      });
      document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = count; });
      cartNeedsRefresh = false;
      reading = false;
      updateCartAvailability();
      if (!busy) restoreCartFocus(bookmark);
      return true;
    } catch {
      if (version !== refreshVersion) return null;
      cartNeedsRefresh = true;
      reading = false;
      updateCartAvailability();
      showCartError(window.llukesStrings.cart_refresh_error);
      return false;
    }
  }

  async function changeQuantity(key, quantity) {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    const bookmark = rememberCartFocus();
    busy = true;
    ++refreshVersion;
    updateCartAvailability();
    let message = null;
    try {
      const { response } = await request(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      if (!response.ok) {
        message = response.status === 422 ? window.llukesStrings.cart_stock_error : window.llukesStrings.cart_uncertain;
      }
    } catch {
      message = window.llukesStrings.cart_network;
    }
    try {
      // Re-read after both success and uncertainty, without repeating the mutation.
      const refreshed = await refreshCart();
      if (message && refreshed === true) showCartError(message);
    } finally {
      busy = false;
      updateCartAvailability();
      restoreCartFocus(bookmark);
    }
  }

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-cart-form]') && (busy || reading || cartNeedsRefresh)) {
      event.preventDefault();
      if (cartNeedsRefresh) showCartError(window.llukesStrings.cart_verify);
    }
  });

  document.addEventListener('close', (event) => {
    if (event.target !== activePanel) return;
    clearTimeout(closeTimer);
    activePanel.classList.remove('is-visible');
    activePanel = null;
    unlockPage();
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-panel-open]');
    if (trigger) {
      event.preventDefault();
      const opening = openPanel(trigger.dataset.panelOpen, trigger);
      if (trigger.dataset.panelOpen === 'llukes-cart') {
        if (activePanel?.id === 'llukes-cart') refreshCart();
        else opening?.then(() => { if (activePanel?.id === 'llukes-cart') refreshCart(); });
      }
    }
    const close = event.target.closest('[data-panel-close]');
    if (close) {
      if (activePanel) closePanel();
      else if (close.closest('.llukes-cart-page')) window.location.href = `${root}collections/aw26-leveil`;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeQuantity(remove.dataset.key, 0); }
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-cart-quantity]')) {
      const input = event.target;
      if (!input.checkValidity() || !input.value) { input.reportValidity(); return; }
      changeQuantity(input.dataset.key, Number(input.value));
    }
  });
  document.querySelectorAll('.llukes-panel').forEach((panel) => {
    panel.addEventListener('cancel', (event) => { event.preventDefault(); closePanel(); });
    panel.addEventListener('click', (event) => {
      const bounds = panel.getBoundingClientRect();
      if (event.target === panel && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) closePanel();
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const elements = [...panel.querySelectorAll(focusable)].filter((element) => element.getClientRects().length && !element.closest('[inert]'));
      if (!elements.length) { event.preventDefault(); return; }
      if (!panel.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? elements.at(-1) : elements[0]).focus(); }
      else if (event.shiftKey && document.activeElement === elements[0]) { event.preventDefault(); elements.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === elements.at(-1)) { event.preventDefault(); elements[0].focus(); }
    });
  });
  document.addEventListener('llukes:cart-updated', (event) => {
    if (event.detail?.open) openPanel('llukes-cart', document.activeElement);
    refreshCart();
  });
  window.addEventListener('pageshow', () => refreshCart());
  document.addEventListener('toggle', (event) => {
    if (event.target.matches('.llukes-faq details[open]')) document.querySelectorAll('.llukes-faq details[open]').forEach((item) => { if (item !== event.target) item.open = false; });
  }, true);

  const privacyDialogSelector = '#shopify-pc__prefs__dialog[role="dialog"][aria-modal="true"]';
  const privacyRootSelector = '#shopify-pc__prefs';
  let observedPrivacyDialog = null;
  let privacySyncQueued = false;
  const privacyVisibilityObserver = new MutationObserver(schedulePrivacySync);

  function privacyDialogVisible(dialog) {
    if (!dialog?.isConnected || !dialog.matches(privacyDialogSelector)) return false;
    for (let element = dialog; element && element !== document.body; element = element.parentElement) {
      if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    }
    return dialog.getClientRects().length > 0;
  }

  function synchronizePrivacy() {
    const dialog = observedPrivacyDialog;
    const closeButton = dialog?.querySelector('#shopify-pc__prefs__header-close');
    if (closeButton && closeButton.getAttribute('aria-label') !== window.llukesStrings.privacy_close) {
      closeButton.setAttribute('aria-label', window.llukesStrings.privacy_close);
    }
    const visible = privacyDialogVisible(dialog);
    if (visible === privacyActive) return;
    if (visible) rememberNativePrivacyStyle(privacyBodyObserver.takeRecords());
    privacyActive = visible;
    if (visible) {
      const requestedTarget = privacyTrigger?.element;
      const candidate = requestedTarget?.isConnected ? requestedTarget : lastOutsidePrivacyFocus;
      privacyReturnTarget = activePanel?.contains(candidate) ? opener : candidate;
      privacyTrigger = null;
      // Transfer ownership before closing a panel so its cleanup cannot unlock the page.
      // With a lightbox, the opening event releases its separate lock before ours is saved.
      const originalState = nativePrivacySnapshot;
      nativePrivacySnapshot = null;
      lockPage('privacy', originalState);
      if (activePanel) closePanel(true, false);
      document.body.classList.add('llukes-privacy-open');
    } else {
      document.body.classList.remove('llukes-privacy-open');
      unlockPage('privacy');
      const target = privacyReturnTarget;
      privacyReturnTarget = null;
      nativePrivacySnapshot = null;
      if (target?.isConnected && !target.closest('[inert]')) target.focus({ preventScroll: true });
    }
  }

  function schedulePrivacySync() {
    if (privacySyncQueued) return;
    privacySyncQueued = true;
    queueMicrotask(() => {
      privacySyncQueued = false;
      synchronizePrivacy();
    });
  }

  function observePrivacyDialog() {
    const dialog = document.querySelector('#shopify-pc__prefs__dialog');
    if (dialog !== observedPrivacyDialog) {
      privacyVisibilityObserver.disconnect();
      observedPrivacyDialog = dialog;
      // Observe only the preferences ancestry, never global home animation styles.
      for (let element = dialog; element && element !== document.body; element = element.parentElement) {
        privacyVisibilityObserver.observe(element, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'aria-modal', 'role'],
        });
      }
    }
    schedulePrivacySync();
  }

  const privacyDiscoveryObserver = new MutationObserver((records) => {
    const changed = records.some((record) => {
      if (record.target instanceof Element && record.target.closest(privacyRootSelector)) return true;
      return [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element && (
        node.matches(privacyRootSelector + ', #shopify-pc__prefs__dialog') ||
        node.querySelector(privacyRootSelector + ', #shopify-pc__prefs__dialog') ||
        (observedPrivacyDialog && node.contains(observedPrivacyDialog))
      ));
    });
    if (changed) observePrivacyDialog();
  });
  privacyDiscoveryObserver.observe(document.body, { childList: true, subtree: true });
  // Shopify writes body overflow before showing the modal. Capture the old inline
  // style from that mutation, rather than saving Shopify's temporary hidden state.
  // This observer does not inspect home descendants or schedule animation work.
  function rememberNativePrivacyStyle(records) {
    if (!document?.body) return;
    if (savedScroll || privacyActive || document.body.classList.contains('he-lightbox-open')) return;
    const body = document.body;
    const nativeLock = body.style.overflow === 'hidden' || body.style.overflowY === 'hidden';
    if (!nativeLock) { nativePrivacySnapshot = null; return; }
    if (!document.getElementById('shopify-pc__prefs') && !privacyRequestPending && !privacyTrigger) return;
    const mutation = records.find((record) => record.attributeName === 'style');
    if (!mutation || nativePrivacySnapshot) return;
    const previousStyle = document.createElement('div').style;
    previousStyle.cssText = mutation.oldValue || '';
    if (previousStyle.position === 'fixed') return;
    nativePrivacySnapshot = { x: window.scrollX, y: window.scrollY, style: mutation.oldValue };
  }
  const privacyBodyObserver = new MutationObserver(rememberNativePrivacyStyle);
  privacyBodyObserver.observe(document.body, { attributes: true, attributeFilter: ['style'], attributeOldValue: true });
  observePrivacyDialog();

  document.addEventListener('focusin', (event) => {
    if (!event.target.closest(privacyRootSelector + ', .he-lightbox')) lastOutsidePrivacyFocus = event.target;
  });
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-privacy-open], #shopify-pc__banner button, #shopify-pc__banner a');
    if (trigger) privacyTrigger = { element: activePanel?.contains(trigger) ? opener : trigger };
    else if (!event.target.closest(privacyRootSelector)) privacyTrigger = null;
  }, true);
  document.addEventListener('click', async (event) => {
    if (!event.target.closest('[data-privacy-open]')) return;
    event.preventDefault();
    if (privacyActive || privacyRequestPending) return;
    privacyRequestPending = true;
    document.querySelectorAll('.llukes-privacy-status').forEach((element) => { element.hidden = true; });
    try {
      if (!window.privacyBanner?.showPreferences) throw new Error('Privacy banner unavailable');
      await window.privacyBanner.showPreferences();
      observePrivacyDialog();
    } catch {
      privacyTrigger = null;
      document.querySelectorAll('.llukes-privacy-status').forEach((element) => {
        element.hidden = false;
        element.textContent = window.llukesStrings.privacy_unavailable;
      });
      // The observer owns the lock only while a real modal is visible.
      observePrivacyDialog();
    } finally {
      privacyRequestPending = false;
    }
  });
})();
